import {
  useReactMutation,
  getPostServicePromiseDeprecated,
  useReactQueryClient
} from '@invision/core';
import type {
  SecurityCloseSessionRequest,
  SecurityCloseSessionResponse
} from 'invApi';

import { AuthenticationStorage, clearAuthenticationApiSession } from '../utils';

interface LogoutOptions {
  /** Custom redirect URL after logout (optional) */
  redirectUrl?: string;
  /** Whether to perform complete cleanup including username (default: true) */
  completeCleanup?: boolean;
}

export function useCloseSessionMutation() {
  const queryClient = useReactQueryClient();

  return useReactMutation<
    SecurityCloseSessionResponse,
    { request?: SecurityCloseSessionRequest; options?: LogoutOptions }
  >(
    ['closeSession'],
    async ({ request = {} }) => {
      // Call the logout API - session info is handled by the service layer
      return getPostServicePromiseDeprecated(
        { path: 'Security/CloseSession' },
        request
      );
    },
    {
      onSettled: (_data, _error, variables) => {
        const { options } = variables;

        // Comprehensive cleanup regardless of API success/failure
        performLogoutCleanup(options?.completeCleanup ?? true);

        // Clear React Query cache
        queryClient.clear();

        // Redirect is handled by the calling component (LogoutPage)
        // to avoid conflicts and provide better UX control
      }
    },
    {
      useLoadingIndicator: true
    }
  );

  function performLogoutCleanup(completeCleanup: boolean) {
    if (completeCleanup) {
      // Clear everything including username and environment preferences
      AuthenticationStorage.clearAllIncludingUsername();
    } else {
      // Preserve username and environment for better UX
      AuthenticationStorage.clearAll();
    }

    // Drop the cached in-memory session so it cannot leak into the next
    // (unauthenticated) request after the user returns to the login page.
    clearAuthenticationApiSession();
  }
}
